import React, { useState, useMemo } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import api, { extractError } from "@/api/client";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Search, Pencil, Trash2, Save, X, Plus, Download, Upload, RefreshCw, Database, Wand2 } from "lucide-react";
import { toast } from "sonner";
import { formatDate, ASSET_TYPES } from "@/lib/dict";
import BulkImportDialog from "@/components/BulkImportDialog";

// Per-table field value → French label overrides, used in grid cells and enum selects.
const FIELD_LABEL_MAPS = { assets: { type: ASSET_TYPES } };

const exampleFor = (type) => {
  if (type === "number") return "100";
  if (type === "boolean") return "true";
  if (type === "date") return "2026-01-31";
  if (type === "email") return "contact@exemple.fr";
  if (type?.startsWith("enum:")) return type.slice(5).split(",")[0];
  if (type?.startsWith("list:")) return "valeur1|valeur2";
  return "Exemple";
};

/**
 * Schema-driven back-office table editor.
 * Reads /api/admin/tables catalog and renders CRUD + bulk-update + bulk-delete UI.
 * Field types are parsed from the backend hints (string, number, boolean, date, email, enum:v1,v2, list:string).
 */
export default function DataEditor() {
  const qc = useQueryClient();
  const { data: catalog = {} } = useQuery({ queryKey: ["admin-catalog"], queryFn: () => api.get("/admin/tables").then((r) => r.data) });
  const tableIds = useMemo(() => Object.keys(catalog), [catalog]);
  const [selectedTable, setSelectedTable] = useState("");
  const activeTable = selectedTable || tableIds[0] || "";

  if (!activeTable) {
    return <Card className="p-8 text-center text-slate-500"><Database size={22} className="mx-auto mb-3 opacity-40" />Chargement du catalogue…</Card>;
  }
  const cfg = catalog[activeTable];
  return (
    <div className="space-y-4">
      <Card className="p-4 border-slate-200">
        <div className="flex items-center gap-3 flex-wrap">
          <div className="flex items-center gap-2 text-xs uppercase tracking-wider font-semibold text-slate-500"><Database size={13} /> Éditeur de données</div>
          <Select value={activeTable} onValueChange={setSelectedTable}>
            <SelectTrigger className="w-72" data-testid="table-selector"><SelectValue /></SelectTrigger>
            <SelectContent>
              {tableIds.map((id) => (
                <SelectItem key={id} value={id}>{catalog[id].label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <div className="text-xs text-slate-500 flex-1">Tables exposées : {tableIds.length}. Modifications journalisées automatiquement.</div>
          <Button size="sm" variant="outline" onClick={() => qc.invalidateQueries({ queryKey: ["admin-rows", activeTable] })}><RefreshCw size={13} className="mr-1.5" />Actualiser</Button>
        </div>
      </Card>
      <TableGrid table={activeTable} cfg={cfg} />
    </div>
  );
}

function TableGrid({ table, cfg }) {
  const qc = useQueryClient();
  const [search, setSearch] = useState("");
  const [skip, setSkip] = useState(0);
  const [selected, setSelected] = useState(new Set());
  const [editRow, setEditRow] = useState(null); // row object or 'new'
  const [bulkOpen, setBulkOpen] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);

  const limit = 50;
  const { data = { total: 0, items: [] }, isFetching } = useQuery({
    queryKey: ["admin-rows", table, search, skip],
    queryFn: () => api.get(`/admin/tables/${table}`, { params: { search: search || undefined, skip, limit } }).then((r) => r.data),
  });

  const editableFields = cfg.editable_fields || {};
  const displayFields = cfg.display_fields || Object.keys(editableFields);
  const readOnly = !!cfg.read_only;

  const toggle = (id) => {
    const s = new Set(selected);
    s.has(id) ? s.delete(id) : s.add(id);
    setSelected(s);
  };
  const toggleAll = () => {
    if (selected.size === data.items.length) setSelected(new Set());
    else setSelected(new Set(data.items.map((r) => r.id)));
  };

  const bulkDelete = useMutation({
    mutationFn: () => api.post(`/admin/tables/${table}/bulk-delete`, { ids: Array.from(selected) }),
    onSuccess: (r) => { toast.success(`${r.data.deleted} ligne(s) supprimée(s)`); setSelected(new Set()); setConfirmDelete(false); qc.invalidateQueries({ queryKey: ["admin-rows", table] }); },
    onError: (e) => toast.error(extractError(e, "Échec")),
  });

  const exportCsv = () => {
    if (data.items.length === 0) return toast.error("Aucune donnée à exporter");
    const cols = Object.keys(data.items[0]);
    const rows = data.items.map((r) => cols.map((c) => JSON.stringify(r[c] ?? "")).join(","));
    const blob = new Blob([[cols.join(","), ...rows].join("\n")], { type: "text/csv" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob); a.download = `${table}.csv`; a.click();
    URL.revokeObjectURL(a.href);
  };

  return (
    <Card className="p-4 border-slate-200">
      {/* Toolbar */}
      <div className="flex items-center gap-2 flex-wrap mb-3">
        <div className="relative flex-1 min-w-[220px]">
          <Search size={14} className="absolute left-2.5 top-2.5 text-slate-400" />
          <Input data-testid="admin-search" value={search} onChange={(e) => { setSearch(e.target.value); setSkip(0); }} placeholder="Recherche..." className="pl-8" />
        </div>
        <div className="text-xs text-slate-500 whitespace-nowrap">{data.total} ligne(s){selected.size > 0 && ` • ${selected.size} sélectionnée(s)`}</div>
        <Button size="sm" variant="outline" data-testid="btn-export-csv" onClick={exportCsv}><Download size={13} className="mr-1.5" />CSV</Button>
        {!readOnly && (
          <>
            {table !== "users" && (
              <Button size="sm" variant="outline" data-testid="btn-import-csv" onClick={() => setImportOpen(true)}><Upload size={13} className="mr-1.5" />Importer CSV</Button>
            )}
            <Button size="sm" variant="outline" data-testid="btn-bulk-edit" onClick={() => setBulkOpen(true)} disabled={selected.size === 0}><Wand2 size={13} className="mr-1.5" />Modifier {selected.size || ""}</Button>
            <Button size="sm" variant="outline" data-testid="btn-bulk-delete" onClick={() => setConfirmDelete(true)} disabled={selected.size === 0} className="text-red-600 border-red-200"><Trash2 size={13} className="mr-1.5" />Supprimer {selected.size || ""}</Button>
            <Button size="sm" data-testid="btn-new-row" onClick={() => setEditRow("new")} className="text-white" style={{ background: "#023E73" }}><Plus size={13} className="mr-1.5" />Nouveau</Button>
          </>
        )}
      </div>

      {/* Table */}
      <div className="overflow-x-auto border border-slate-100 rounded-md">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 border-b border-slate-200">
            <tr>
              {!readOnly && <th className="w-9 px-2 py-2"><Checkbox data-testid="row-select-all" checked={selected.size > 0 && selected.size === data.items.length} onCheckedChange={toggleAll} /></th>}
              {displayFields.map((f) => <th key={f} className="text-left px-3 py-2 text-[11px] uppercase tracking-wider font-semibold text-slate-500">{f}</th>)}
              <th className="w-14 px-2 py-2"></th>
            </tr>
          </thead>
          <tbody>
            {isFetching && <tr><td colSpan={displayFields.length + 2} className="text-center text-slate-400 py-6">Chargement…</td></tr>}
            {!isFetching && data.items.length === 0 && <tr><td colSpan={displayFields.length + 2} className="text-center text-slate-400 py-8">Aucune ligne.</td></tr>}
            {!isFetching && data.items.map((row) => (
              <tr key={row.id} data-testid={`row-${row.id}`} className={`border-b border-slate-100 hover:bg-slate-50 transition ${selected.has(row.id) ? "bg-blue-50/40" : ""}`}>
                {!readOnly && <td className="px-2 py-2"><Checkbox data-testid={`row-check-${row.id}`} checked={selected.has(row.id)} onCheckedChange={() => toggle(row.id)} /></td>}
                {displayFields.map((f) => {
                  const labelMap = FIELD_LABEL_MAPS[table]?.[f];
                  const raw = row[f];
                  const display = labelMap && typeof raw === "string" ? (labelMap[raw] || raw) : raw;
                  return <td key={f} className="px-3 py-2 max-w-[240px] truncate text-slate-700">{renderCell(display)}</td>;
                })}
                <td className="px-2 py-2 text-right">
                  {!readOnly && <button data-testid={`row-edit-${row.id}`} onClick={() => setEditRow(row)} className="p-1.5 rounded hover:bg-slate-100"><Pencil size={13} /></button>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      <div className="flex items-center justify-between mt-3 text-xs text-slate-500">
        <div>Affichage {skip + 1}–{Math.min(skip + limit, data.total)} / {data.total}</div>
        <div className="flex gap-2">
          <Button size="sm" variant="outline" disabled={skip === 0} onClick={() => setSkip(Math.max(0, skip - limit))}>Précédent</Button>
          <Button size="sm" variant="outline" disabled={skip + limit >= data.total} onClick={() => setSkip(skip + limit)}>Suivant</Button>
        </div>
      </div>

      {/* Row editor dialog */}
      {editRow && (
        <RowEditor table={table} row={editRow === "new" ? null : editRow} editableFields={editableFields} onClose={() => setEditRow(null)} />
      )}

      {/* Bulk update dialog */}
      {bulkOpen && (
        <BulkEditor table={table} editableFields={editableFields} ids={Array.from(selected)} onClose={() => { setBulkOpen(false); setSelected(new Set()); }} />
      )}

      {/* CSV bulk import (template per table generated from the schema catalog) */}
      {importOpen && (
        <BulkImportDialog
          open={importOpen}
          onClose={() => setImportOpen(false)}
          entity={table}
          title={cfg.label}
          endpoint={`/admin/tables/${table}/bulk-import`}
          columns={Object.entries(cfg.editable_fields || {}).map(([key, type]) => ({
            key,
            label: key,
            required: (cfg.required_fields || []).includes(key),
            example: exampleFor(type),
          }))}
          onDone={() => qc.invalidateQueries({ queryKey: ["admin-rows", table] })}
        />
      )}

      {/* Bulk delete confirmation */}
      <Dialog open={confirmDelete} onOpenChange={setConfirmDelete}>
        <DialogContent data-testid="confirm-delete-dialog">
          <DialogHeader><DialogTitle>Supprimer {selected.size} ligne(s) ?</DialogTitle><DialogDescription>Cette action est irréversible. La suppression sera journalisée dans le journal d&apos;audit.</DialogDescription></DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmDelete(false)}>Annuler</Button>
            <Button data-testid="btn-confirm-delete" className="bg-red-600 text-white hover:bg-red-700" onClick={() => bulkDelete.mutate()} disabled={bulkDelete.isPending}>{bulkDelete.isPending ? "Suppression..." : "Confirmer la suppression"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}

function renderCell(v) {
  if (v === null || v === undefined || v === "") return <span className="text-slate-300">—</span>;
  if (typeof v === "boolean") return v ? "✓" : "✗";
  if (Array.isArray(v)) return v.length === 0 ? <span className="text-slate-300">[]</span> : <Badge variant="outline" className="text-[10px]">{v.length} • {v.slice(0, 2).join(", ")}{v.length > 2 ? "…" : ""}</Badge>;
  if (typeof v === "object") return <span className="font-mono text-[10px] text-slate-500">{JSON.stringify(v).slice(0, 40)}</span>;
  if (typeof v === "string" && v.length >= 20 && v.match(/^\d{4}-\d{2}-\d{2}/)) return formatDate(v);
  return String(v);
}

function FieldInput({ name, type, value, onChange, labelMap }) {
  if (type === "boolean") {
    return <div className="flex items-center gap-2"><Checkbox checked={!!value} onCheckedChange={onChange} data-testid={`field-${name}`} /><span className="text-xs text-slate-600">{value ? "Oui" : "Non"}</span></div>;
  }
  if (type === "number") {
    return <Input data-testid={`field-${name}`} type="number" value={value ?? ""} onChange={(e) => onChange(e.target.value === "" ? null : Number(e.target.value))} />;
  }
  if (type === "date") {
    const v = (value || "").slice(0, 10);
    return <Input data-testid={`field-${name}`} type="date" value={v} onChange={(e) => onChange(e.target.value || null)} />;
  }
  if (type === "email") {
    return <Input data-testid={`field-${name}`} type="email" value={value ?? ""} onChange={(e) => onChange(e.target.value)} />;
  }
  if (type?.startsWith("enum:")) {
    const opts = type.slice(5).split(",");
    return (
      <Select value={value ?? ""} onValueChange={onChange}>
        <SelectTrigger data-testid={`field-${name}`}><SelectValue placeholder="Sélectionner..." /></SelectTrigger>
        <SelectContent>{opts.map((o) => <SelectItem key={o} value={o}>{labelMap?.[o] || o}</SelectItem>)}</SelectContent>
      </Select>
    );
  }
  if (type?.startsWith("list:")) {
    const arr = Array.isArray(value) ? value : (value ? [value] : []);
    return <Input data-testid={`field-${name}`} value={arr.join(", ")} onChange={(e) => onChange(e.target.value.split(",").map((s) => s.trim()).filter(Boolean))} placeholder="valeur1, valeur2" />;
  }
  // Big text areas for description-like fields
  if (name === "description" || name === "notes" || name === "body") {
    return <Textarea data-testid={`field-${name}`} rows={3} value={value ?? ""} onChange={(e) => onChange(e.target.value)} />;
  }
  return <Input data-testid={`field-${name}`} value={value ?? ""} onChange={(e) => onChange(e.target.value)} />;
}

function RowEditor({ table, row, editableFields, onClose }) {
  const qc = useQueryClient();
  const isNew = !row;
  const initial = useMemo(() => {
    const o = {};
    Object.keys(editableFields).forEach((f) => { o[f] = row?.[f] ?? null; });
    return o;
  }, [row, editableFields]);
  const [values, setValues] = useState(initial);

  const save = useMutation({
    mutationFn: () => {
      // Only send changed fields when editing to keep payloads minimal and audit precise.
      const dirty = {};
      Object.keys(values).forEach((k) => {
        const v = values[k]; const orig = row?.[k] ?? null;
        if (JSON.stringify(v) !== JSON.stringify(orig)) dirty[k] = v;
      });
      if (isNew) return api.post(`/admin/tables/${table}`, values);
      if (Object.keys(dirty).length === 0) return Promise.resolve({ data: row });
      return api.patch(`/admin/tables/${table}/${row.id}`, dirty);
    },
    onSuccess: () => { toast.success(isNew ? "Créé" : "Enregistré"); qc.invalidateQueries({ queryKey: ["admin-rows", table] }); onClose(); },
    onError: (e) => toast.error(extractError(e, "Échec")),
  });

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="sm:max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="row-editor-dialog">
        <DialogHeader>
          <DialogTitle>{isNew ? "Nouvelle ligne" : `Éditer ${row.reference || row.name || row.email || row.id?.slice(0, 8)}`}</DialogTitle>
          <DialogDescription>Table {table} — {Object.keys(editableFields).length} champ(s) modifiable(s). Les champs sensibles (mot de passe, id...) sont protégés.</DialogDescription>
        </DialogHeader>
        <div className="grid md:grid-cols-2 gap-3 py-2">
          {Object.entries(editableFields).map(([name, type]) => (
            <div key={name} className={name === "description" || name === "notes" || name === "body" ? "md:col-span-2" : ""}>
              <Label className="text-xs text-slate-500 truncate block" title={`${name} (${type})`}>{name} <span className="text-slate-300 text-[10px]">({type.length > 30 ? type.slice(0, 27) + "…" : type})</span></Label>
              <FieldInput name={name} type={type} value={values[name]} onChange={(v) => setValues({ ...values, [name]: v })} labelMap={FIELD_LABEL_MAPS[table]?.[name]} />
            </div>
          ))}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}><X size={13} className="mr-1.5" />Annuler</Button>
          <Button data-testid="btn-save-row" className="text-white" style={{ background: "#023E73" }} onClick={() => save.mutate()} disabled={save.isPending}>
            <Save size={13} className="mr-1.5" />{save.isPending ? "Enregistrement..." : "Enregistrer"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function BulkEditor({ table, editableFields, ids, onClose }) {
  const qc = useQueryClient();
  const [field, setField] = useState("");
  const [val, setVal] = useState(null);
  const [confirmed, setConfirmed] = useState(false);
  const type = editableFields[field];
  const apply = useMutation({
    mutationFn: () => api.post(`/admin/tables/${table}/bulk-update`, { ids, updates: { [field]: val } }),
    onSuccess: (r) => { toast.success(`${r.data.modified} ligne(s) mises à jour`); qc.invalidateQueries({ queryKey: ["admin-rows", table] }); onClose(); },
    onError: (e) => toast.error(extractError(e, "Échec")),
  });
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent data-testid="bulk-editor-dialog">
        <DialogHeader>
          <DialogTitle>Modifier {ids.length} ligne(s)</DialogTitle>
          <DialogDescription>Sélectionnez un champ et sa nouvelle valeur — elle s&apos;appliquera à toute la sélection. Journalisée dans l&apos;audit.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3 py-2">
          <div>
            <Label>Champ à modifier</Label>
            <Select value={field} onValueChange={(v) => { setField(v); setVal(null); }}>
              <SelectTrigger data-testid="bulk-field-selector"><SelectValue placeholder="Choisir un champ..." /></SelectTrigger>
              <SelectContent>{Object.entries(editableFields).map(([k, t]) => <SelectItem key={k} value={k}>{k} ({t})</SelectItem>)}</SelectContent>
            </Select>
          </div>
          {field && (
            <div>
              <Label>Nouvelle valeur</Label>
              <FieldInput name={field} type={type} value={val} onChange={setVal} labelMap={FIELD_LABEL_MAPS[table]?.[field]} />
            </div>
          )}
          {field && (
            <div className="flex items-center gap-2 p-2 rounded bg-amber-50 border border-amber-200 text-xs text-amber-900">
              <Checkbox data-testid="bulk-confirm-check" checked={confirmed} onCheckedChange={setConfirmed} />
              Je confirme la mise à jour de <b>{ids.length}</b> ligne(s) avec la valeur ci-dessus.
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Annuler</Button>
          <Button data-testid="btn-apply-bulk" className="text-white" style={{ background: "#023E73" }} disabled={!field || !confirmed || apply.isPending} onClick={() => apply.mutate()}>
            {apply.isPending ? "Application..." : "Appliquer"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
